import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type KeyboardEvent as EventoDeTeclado,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, ErrorBanner, PageHeader, TextInput, useToast } from '@zarya/ui';
import { Icon } from '@zarya/theme';
import { useCurrentUser } from '@app/auth/useCurrentUser';
import {
  countDivergences,
  LENS_EMPTY,
  LENS_PENDING,
  lensFromParams,
  lensMalformed,
  lensParams,
  useAccessMap,
  type LensSelection,
} from './AccessMap.api';
import { DICA, Graph } from './Graph';
import { DetailPanel } from './DetailPanel';
import { DivergencesCard } from './DivergencesCard';
import { LensBar } from './LensBar';
import { useAvailableHeight } from './useAvailableHeight';
import { useGraphLayout } from './useGraphLayout';
import {
  AM_TYPE,
  PROPOSITO,
  LEGENDA_KINDS,
  lensLabels,
  typeLabel,
  withLensLabels,
} from './accessMapVocab';
import { computedLabel, pendingLabel } from './freshness';
import s from './AccessMap.module.css';
import { t, tn } from '@app/i18n';

/**
 * T19 — Mapa de acesso (FR-102 a FR-105).
 *
 * "Quem acessa quem pelo CONTRATO." O grafo é montado com dados locais
 * (contratos, resources); as **divergências** vêm da leitura de acesso do
 * executor e, quando ela falha, o mapa continua — sem elas, e dizendo isso.
 *
 * SEM POLÍTICAS (04/09). O toggle «Políticas como nós» e a contagem «N políticas
 * ativas» saíram junto com os nós que o servidor deixou de emitir: Human Access
 * não concede acesso à identidade, e desenhar `identidade → política → resource`
 * era afirmar o contrário. A pessoa entra no mapa pela lente «Ver como».
 *
 * Sem os botões `+ / ▾` do protótipo (corte do §4 do PRD): o zoom é pela roda e
 * pelo duplo-clique. O `Enquadrar`, também cortado, voltou em 29/08 como
 * **Ajustar à tela**: o card passou a ter altura fixa, e um mapa deixado fora de
 * lugar não tem mais como ser consertado rolando a página.
 */

const TOAST_LAYOUT = () => t('accessMap.toolbar.layoutRestored');

/** As duas abas do painel da direita — antes eram dois cards empilhados. */
type PainelTab = 'detalhe' | 'divergencias';
const ABAS_DO_PAINEL: PainelTab[] = ['detalhe', 'divergencias'];

/**
 * Chave da mensagem por código de erro da lente (o `error_code` do servidor). A
 * tabela guarda a CHAVE e `lensFailure()` resolve no desenho: era um `Record` com
 * `t()` avaliado no import, e congelava o idioma da primeira renderização — os dois
 * vizinhos já eram função, este ficou para trás (qa-i18n, 09/09).
 */
const LENS_FAILURE_KEY: Record<string, string> = {
  rule_not_found: 'accessMap.lens.ruleNotFound',
  simulator_unavailable: 'accessMap.lens.simulatorUnavailable',
  lens_not_evaluated: 'accessMap.lens.notEvaluated',
};

const LENS_FAILURE_DEFAULT = () => t('accessMap.lens.failureDefault');

function lensFailure(code: string): string {
  const chave = LENS_FAILURE_KEY[code];
  return chave ? t(chave) : LENS_FAILURE_DEFAULT();
}

/** `?view_as=` que não é `claims` nem UUID — link truncado, copiado pela metade. */
const LENS_MALFORMED = () => t('accessMap.lens.malformed');

export function AccessMapPage() {
  const toast = useToast();

  /**
   * A lente vive na URL (`?view_as=`) — decisão do §9: o link é compartilhável
   * («o mapa como auditor» é uma coisa que se manda por mensagem). A URL é a
   * fonte, e não um estado paralelo: com `useState` ao lado, voltar no
   * navegador mudaria o endereço e não a tela.
   */
  const [params, setParams] = useSearchParams();
  const lensURL = lensFromParams(params);
  // Lente malformada não vai ao servidor: ela viraria 422 e levaria o mapa
  // junto. Aqui ela vira mapa base + aviso inline. Ver `lensMalformed`.
  const malformedLens = lensMalformed(lensURL);
  const lens = malformedLens ? LENS_EMPTY : lensURL;
  const mapping = useAccessMap(lens);
  // Os nomes da lente no idioma da tela (aim-6ph.23): o servidor escreve os da API, em inglês. Memoizado pelos
  // rótulos, que mudam com o idioma: o desenho do grafo depende da identidade do mapa.
  const { adHocLabel, grantLabel } = lensLabels();
  const data = useMemo(
    () => withLensLabels(mapping.data, { adHocLabel, grantLabel }),
    [mapping.data, adHocLabel, grantLabel],
  );
  /**
   * OCUPADO (05/09): uma leitura que vai mudar o mapa está correndo e o que se
   * vê não é ela — ou é o mapa anterior (`placeholderData`, troca de lente; ver
   * `useAccessMap`) ou não é nada (primeira abertura). Nos dois casos a tela é a
   * MESMA: a moldura fica (título, lente, painel lateral, legenda), a área do
   * mapa diz o que está fazendo, a área de conteúdo (tudo menos o menu lateral)
   * bloqueia cliques e a lente fica desligada — clicar em «Ver como» no meio do
   * cálculo enfileiraria pedidos que se atropelam, e o mapa que chegasse por
   * último não seria o último pedido.
   *
   * UM caminho de carregamento, e não dois (pedido do mrolim, 05/09): a primeira
   * abertura deixava a tela inteira em «Montando…» e a moldura só nascia com o
   * grafo. Agora ela é a troca de lente sem mapa anterior — só o texto muda.
   */
  const busy = mapping.isFetching && (mapping.isPlaceholderData || mapping.data === undefined);
  /** Primeira abertura: não há mapa anterior a manter. */
  const firstLoad = busy && mapping.data === undefined;

  function changeLens(next: LensSelection) {
    const seguintes = new URLSearchParams(params);
    seguintes.delete('view_as');
    seguintes.delete('claim');
    for (const [chave, valor] of lensParams(next)) {
      seguintes.append(chave, valor);
    }
    // `replace`: escolher perfil no seletor não é navegação, e empilhar cada
    // tentativa faria o «voltar» percorrer os perfis um a um.
    setParams(seguintes, { replace: true });
  }
  // As posições arrastadas ficam por tenant (FR-102): a chave é o cliente da
  // sessão, e `default` só vale enquanto o `getMe` não respondeu.
  const { tenant } = useCurrentUser();

  const [focar, setFocar] = useState(false);
  const [search, setBusca] = useState('');
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [tab, setAba] = useState<PainelTab>('detalhe');
  /**
   * Contador de **Ajustar à tela**: o `Graph` reenquadra a cada incremento. É
   * contador porque o pedido é um evento — dois cliques seguidos (depois de
   * aproximar de novo no meio) têm de enquadrar duas vezes.
   */
  const [settingSolicitado, setAjusteSolicitado] = useState(0);

  const { layout, mover, reorganizar, arrastado } = useGraphLayout(data, {
    tenant: tenant?.client,
  });

  // O card vai do topo do conteúdo até quase a base da janela; a altura é medida
  // porque o que está acima dele não tem tamanho fixo (ver `useAlturaDisponivel`).
  const { ref: quadro, altura } = useAvailableHeight();

  // Esc limpa a seleção (FR-102), como o clique no fundo.
  useEffect(() => {
    function aoTeclar(event: KeyboardEvent) {
      if (event.key === 'Escape') setSelecionado(null);
    }
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, []);

  const nos = data?.nodes ?? [];
  const arestas = data?.edges ?? [];
  const identidades = nos.filter((no) => no.kind === 'identidade').length;
  const resources = nos.filter((no) => no.kind === 'resource').length;
  const noSelecionado = nos.find((no) => no.id === selecionado) ?? null;

  const lensState = data?.lens;
  const withLens = lensState?.active === true;
  const lensAlcanca = arestas.filter((edge) => edge.reach?.reached).length;

  // Sem contagem de política (04/09): o mapa é o eixo identidade, e anunciar
  // «N políticas ativas» num mapa que não as desenha prometia uma coluna.
  const metadata = firstLoad
    ? t('accessMap.graph.building')
    : [
        // `tn` no lugar dos três ternários: a regra de plural sai do catálogo, e
        // pt-BR e en não a compartilham (o 0 do português cai em `one`).
        tn('accessMap.meta.identities', identidades),
        tn('accessMap.meta.resources', resources),
        // Com a lente ligada a linha diz o que ela alcança: é o número que responde
        // «e daí?» depois de escolher o perfil. Ela só muda quando o operador troca
        // a lente — e aí a leitura inteira é refeita de qualquer jeito.
        withLens
          ? [
              t('accessMap.meta.lensPrefix', { label: lensState?.label ?? '—' }),
              tn('accessMap.meta.reachedPaths', lensAlcanca),
            ].join(' · ')
          : null,
        // O mapa guardado (mapa-acesso, 04/10): de quando é o que se vê.
        mapping.data ? computedLabel(mapping.data) : null,
      ]
        .filter(Boolean)
        .join(' · ');
  /**
   * HÁ MUDANÇA AINDA NÃO CALCULADA (mapa-acesso, 04/10): o mapa que se vê continua
   * válido e clicável — não é a troca de lente, que bloqueia —, e o chip diz desde
   * quando há mudança. A leitura se repete (`useAccessMap`) até o servidor dizer que
   * está em dia, e aí o mapa troca sozinho.
   */
  const pending = busy ? null : pendingLabel(mapping.data);

  /**
   * O que a tela está fazendo com o nó selecionado (fidelidade #B22) — na dica
   * SOBRE o palco, e não no cabeçalho.
   *
   * POR QUE SAIU DO CABEÇALHO (03/09, medido no E2E de T19)
   *
   *	No cabeçalho a frase entra e sai a cada clique, e com ela a linha de
   *	metadados quebra em duas linhas e volta a uma. Isso move o card **20 px** —
   *	e o segundo clique de um duplo-clique cai no fundo, não no nó: o gesto
   *	termina com o mapa ampliado e a seleção perdida, que é exatamente o Bug 3b
   *	que o `Graph` existe para não ter. O defeito é anterior à Fase 3; o que
   *	mudou foi a coluna do nó, que passou a cair onde 20 px importam.
   *
   *	A dica é `position: absolute` sobre o palco: o texto dela muda sem mover
   *	nada. A informação continua na tela, e mais perto do grafo do que estava.
   */
  const hint = noSelecionado
    ? focar
      ? t('accessMap.meta.onlyPathOf', { label: noSelecionado.label })
      : t('accessMap.meta.highlightedPath', { label: noSelecionado.label })
    : DICA();

  const divergencias = mapping.data ? countDivergences(mapping.data) : null;
  const tabLabel: Record<PainelTab, string> = {
    detalhe: t('accessMap.tab.detail'),
    // A contagem no rótulo (FR-104) é o que diz que vale abrir a aba. Com o
    // mapa, as listas vêm sempre (aim-6ph.18). O «—» fica só na primeira
    // leitura, com a moldura na tela e o mapa ainda a caminho: ali não há
    // número a mostrar. Por isso `t` e não `tn`: o «—» não tem plural.
    divergencias: t('accessMap.tab.divergences', { n: divergencias ?? '—' }),
  };

  /**
   * Selecionar um nó traz o **Detalhe** à frente: com a aba de Divergências
   * aberta, clicar no grafo não teria resposta visível nenhuma. Limpar a
   * seleção (Esc, clique no fundo) não mexe na aba — o vazio do Detalhe já diz
   * o que fazer, e trocar de aba sozinho tiraria de baixo do usuário a lista
   * que ele estava lendo.
   */
  function selecionarNo(id: string | null) {
    setSelecionado(id);
    if (id) setAba('detalhe');
  }

  /** Setas percorrem as abas, como manda o padrão de `tablist`. */
  function toTeclarInTab(event: EventoDeTeclado<HTMLButtonElement>, index: number) {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (index + step + ABAS_DO_PAINEL.length) % ABAS_DO_PAINEL.length;
    setAba(ABAS_DO_PAINEL[next]);
    // O foco acompanha a seleção (seleção automática): senão o Tab seguinte
    // sairia da aba que acabou de abrir.
    const abas =
      event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    abas?.[next]?.focus();
  }

  return (
    <>
      <PageHeader
        title={t('accessMap.page.title')}
        tip={PROPOSITO()}
        // `exchange`, como o `ph('exchange', 'Mapa de acesso', …)` do protótipo.
        icon="exchange"
        updatedLabel={metadata}
        actions={
          <span className={s.controles}>
            <button
              type="button"
              data-testid="controle-ajustar"
              className={s.pill}
              // Volta o mapa ao enquadramento automático depois de aproximar,
              // mover ou arrastar. Com o card de altura fixa, um mapa fora de
              // lugar não tem mais como ser consertado rolando a página — é o
              // `Enquadrar` do protótipo, cortado no §4 e de volta em 29/08.
              onClick={() => setAjusteSolicitado((current) => current + 1)}
            >
              {t('accessMap.toolbar.fit')}
            </button>
            <button
              type="button"
              data-testid="controle-focar"
              className={[s.pill, focar ? s.pillOn : ''].filter(Boolean).join(' ')}
              aria-pressed={focar}
              onClick={() => setFocar((current) => !current)}
            >
              {selecionado
                ? t('accessMap.toolbar.focusSelected')
                : t('accessMap.toolbar.focusOnSelect')}
            </button>
            {arrastado ? (
              <Button
                data-testid="controle-reorganizar"
                variant="out"
                size="sm"
                icon="refresh"
                onClick={() => {
                  reorganizar();
                  toast.mostrar(TOAST_LAYOUT(), { tone: 'info' });
                }}
              >
                {t('accessMap.toolbar.rearrange')}
              </Button>
            ) : null}
          </span>
        }
      />

      {/* A lente fica no TOPO da tela, e não dentro do card do grafo: ela é a
          pergunta ("ver como quem?"), e o card é a resposta. */}
      {/* Na primeira abertura a lente já aparece, desligada e sem regras
          (`LENS_PENDING`): a moldura não espera o grafo. */}
      {lensState || busy ? (
        <LensBar
          selection={lens}
          lens={lensState ?? LENS_PENDING}
          onChange={changeLens}
          busy={busy}
        />
      ) : null}

      {malformedLens ? (
        <Alert tone="warning" title={t('accessMap.lens.failedTitle')}>
          {LENS_MALFORMED()}
        </Alert>
      ) : null}

      {lensState?.unavailable ? (
        <Alert tone="warning" title={t('accessMap.lens.failedTitle')}>
          {lensFailure(lensState.error_code ?? '')}
        </Alert>
      ) : null}

      {mapping.isError ? (
        <ErrorBanner
          error={mapping.error}
          title={t('accessMap.page.error')}
          onRetry={() => void mapping.refetch()}
        />
      ) : null}

      {!busy && !mapping.isError && nos.length === 0 ? (
        <Card title={t('accessMap.page.emptyTitle')}>
          <div className={s.painelVazio}>
            <Icon name="exchange" size="xl" aria-hidden="true" />
            <p className={s.vazioTexto} data-testid="mapa-vazio">
              {t('accessMap.page.emptyBody')}
            </p>
          </div>
        </Card>
      ) : null}

      {nos.length > 0 || busy ? (
        // Um card só, de altura fixa: o mapa à esquerda e os dois painéis
        // unidos em abas à direita. `--am-altura` (e não `height` inline) para
        // que a media query do empilhamento ainda consiga devolvê-la a `auto`.
        <div
          ref={quadro}
          className={s.quadro}
          style={altura === null ? undefined : ({ '--am-altura': `${altura}px` } as CSSProperties)}
        >
          <Card bare fill className={s.cartao} aria-label={t('accessMap.graph.regionLabel')}>
            <div className={s.secoes}>
              <section
                className={s.mapa}
                aria-label={t('accessMap.graph.label')}
                aria-busy={busy || undefined}
              >
                <div className={s.barraDoMapa}>
                  {/* Legenda do protótipo (`.amlegend`): o mesmo disco do nó,
                      com o mesmo par fundo/tinta — é a chave de leitura do
                      desenho, e ela só serve se a moeda for a mesma nos dois
                      temas. */}
                  <div className={s.legenda} aria-label={t('accessMap.graph.legendLabel')}>
                    {/* O disco de pessoa só existe sob a lente. Mostrá-lo na
                        legenda do mapa padrão prometeria um nó que ele não tem. */}
                    {LEGENDA_KINDS.filter((tipo) => tipo !== 'human' || withLens).map((tipo) => (
                      <span key={tipo} className={s.legendaItem}>
                        <span className={s.legendaCor} style={{ background: AM_TYPE[tipo].cor }}>
                          <Icon name={AM_TYPE[tipo].icone} size="xs" aria-hidden="true" />
                        </span>
                        {typeLabel(tipo)}
                      </span>
                    ))}
                  </div>
                  {/* O MESMO indicador nos dois casos; só o texto diz se há mapa
                      anterior por baixo («Recalculando») ou não («Montando»). */}
                  {busy ? (
                    <span
                      className={s.recalculando}
                      role="status"
                      data-testid={firstLoad ? 'mapa-carregando' : 'mapa-recalculando'}
                    >
                      {firstLoad ? t('accessMap.graph.building') : t('accessMap.graph.rebuilding')}
                    </span>
                  ) : null}
                  {/* O MESMO chip, com outro texto: mudança ainda não calculada. */}
                  {pending ? (
                    <span className={s.recalculando} role="status" data-testid="mapa-atualizando">
                      {pending}
                    </span>
                  ) : null}
                  <span className={s.realce}>
                    <TextInput
                      type="search"
                      aria-label={t('accessMap.toolbar.highlightByName')}
                      placeholder={t('accessMap.toolbar.highlightByName')}
                      value={search}
                      onChange={(event) => setBusca(event.target.value)}
                    />
                  </span>
                </div>

                <div className={s.palco}>
                  <Graph
                    layout={layout}
                    selecionado={selecionado}
                    onSelecionar={selecionarNo}
                    onMover={mover}
                    busca={search}
                    focar={focar}
                    ajusteSolicitado={settingSolicitado}
                  />
                  {/* Dentro do palco: no protótipo a dica é `.amhint`,
                      flutuando sobre o desenho, não uma linha abaixo dele. */}
                  <p className={s.dica} data-testid="map-hint">
                    {hint}
                  </p>
                </div>
              </section>

              <section className={s.lado} aria-label={t('accessMap.graph.detailAndDivergences')}>
                <div
                  className={s.abasPaineis}
                  role="tablist"
                  aria-label={t('accessMap.graph.panelsLabel')}
                >
                  {ABAS_DO_PAINEL.map((id, index) => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      id={`aba-painel-${id}`}
                      data-testid={`aba-painel-${id}`}
                      aria-selected={tab === id}
                      aria-controls={`painel-do-mapa-${id}`}
                      // Foco rovendo: a tira inteira é **uma** parada de Tab.
                      tabIndex={tab === id ? 0 : -1}
                      className={[s.abaPainel, tab === id ? s.abaPainelOn : '']
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setAba(id)}
                      onKeyDown={(event) => toTeclarInTab(event, index)}
                    >
                      {tabLabel[id]}
                    </button>
                  ))}
                </div>

                {/* Só a aba aberta rola — trocar de aba não muda a altura do card. */}
                <div
                  className={s.conteudoAba}
                  role="tabpanel"
                  id={`painel-do-mapa-${tab}`}
                  aria-labelledby={`aba-painel-${tab}`}
                  // Região rolável alcançável pelo teclado (WCAG 2.1.1).
                  tabIndex={0}
                >
                  {tab === 'detalhe' ? (
                    <DetailPanel
                      className={s.painelEmbutido}
                      embutido
                      no={noSelecionado}
                      arestas={arestas}
                      nos={nos}
                      onFechar={() => setSelecionado(null)}
                    />
                  ) : (
                    <DivergencesCard className={s.painelEmbutido} mapa={mapping.data} />
                  )}
                </div>
              </section>
            </div>
          </Card>
        </div>
      ) : null}

      {/* A camada que bloqueia cliques enquanto o mapa monta ou a lente
          recalcula. Invisível — o mapa anterior, quando há, continua legível — e
          fora da árvore do card, porque cobre a área de conteúdo inteira, não só
          o mapa. */}
      {busy ? <div className={s.bloqueio} data-testid="mapa-bloqueio" aria-hidden="true" /> : null}
    </>
  );
}
